<template>
    <div>
        <kgr-note-box
            v-if="noteBoxParams !== null"
            warning
            html
            :title-key="noteBoxParams.title"
            :text-key="noteBoxParams.text"
        />

        <template v-if="!config.isIncomplete()">
            <br />
            <kgr-month-of-year-select
                v-model="inPeriods"
                :start="inPeriodRangeStart"
                :end="inPeriodRangeEnd"
                allow-multiple
                @post-input="handleMonthOfYearSelect"
            />
            <kgr-loading v-if="loading" />
            <div v-else>
                <kgr-note-box
                    v-if="inPeriods.length > 0 && currentReports.length === 0"
                    text-key="basfi.noReport.note.text"
                    title-key="basfi.noReport.note.title"
                />
                <kgr-note-box
                    v-if="previewSelected && config.isEnabled()"
                    text-key="basfi.previewReport.note.text"
                    title-key="basfi.previewReport.note.title"
                    :text-params="{nextReportDate: config.getNextReportDate().format('DD.MM.YYYY')}"
                />
                <kgr-note-box
                    v-else-if="previewSelected && config.isPaused()"
                    text-key="basfi.previewReport.paused.note.text"
                    title-key="basfi.previewReport.paused.note.title"
                />
                <kgr-note-box
                    v-if="currentSingleReport !== null && !previewSelected"
                    text-key="basfi.report.note.text"
                    title-key="basfi.report.note.title"
                    :text-params="{created: currentSingleReport.created.toLocaleString('de-DE')}"
                />
                <br />
                <div v-if="currentReports.length > 0">
                    <kgr-table class="kgr-table--block">
                        <thead>
                            <tr class="kgr-table__row">
                                <kgr-th sortable>
                                    {{ $t('Kind') }}
                                </kgr-th>
                                <kgr-th sortable>
                                    {{ $t('basfi.kindergartenReport.tableheader.start') }}
                                </kgr-th>
                                <kgr-th sortable>
                                    {{ $t('basfi.kindergartenReport.tableheader.end') }}
                                </kgr-th>
                                <kgr-th sortable>
                                    {{ $t('basfi.kindergartenReport.tableheader.subventionNumber') }}
                                </kgr-th>
                                <kgr-th
                                    v-if="inPeriods.length > 1"
                                    sortable
                                >
                                    {{
                                        $t('basfi.kindergartenReport.tableheader.inPeriod')
                                    }}
                                </kgr-th>
                                <kgr-th
                                    v-if="inPeriods.length > 1"
                                    sortable
                                >
                                    {{
                                        $t('basfi.kindergartenReport.tableheader.created')
                                    }}
                                </kgr-th>
                                <kgr-th sortable>
                                    {{ $t('Status') }}
                                </kgr-th>
                                <kgr-th sortable>
                                    {{ $t('basfi.kindergartenReport.tableheader.processLog') }}
                                </kgr-th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="report in currentReports">
                                <tr
                                    v-for="announcement in report.announcements"
                                    :key="announcement.id"
                                    class="kgr-table__row"
                                >
                                    <td class="kgr-table__cell">
                                        <a
                                            :href="childUrl(announcement.child.id)"
                                            class="kgr-link kgr-link--unobtrusive"
                                        >{{ announcement.child.name }}</a>
                                    </td>
                                    <td class="kgr-table__cell">
                                        {{ announcement.start ? announcement.start.toString() : '' }}
                                    </td>
                                    <td class="kgr-table__cell">
                                        {{ announcement.end ? announcement.end.toString() : '' }}
                                    </td>
                                    <td class="kgr-table__cell">
                                        {{ announcement.childNumber }} / {{ announcement.applicationNumber }}
                                    </td>
                                    <td
                                        v-if="inPeriods.length > 1"
                                        class="kgr-table__cell"
                                    >
                                        {{
                                            report.inPeriod.toString()
                                        }}
                                    </td>
                                    <td
                                        v-if="inPeriods.length > 1"
                                        class="kgr-table__cell"
                                    >
                                        <em
                                            v-if="report === previewReport"
                                            class="kgr-text kgr-text--italic"
                                        >{{ $t('basfi.report.preview') }}</em>
                                        <template v-else>
                                            {{ report.created.toLocaleString('de-DE') }}
                                        </template>
                                    </td>
                                    <td
                                        class="kgr-table__cell kgr-table__cell--actions"
                                        :data-sort="report === previewReport ? 1 : 0"
                                    >
                                        <kgr-icon-active
                                            v-if="report === previewReport"
                                            disabled
                                            small
                                            :title="$t('basfi.announcement.notSent')"
                                        />
                                        <kgr-icon-active
                                            v-else
                                            small
                                            :title="$t('basfi.announcement.sent')"
                                        />
                                    </td>
                                    <td class="kgr-table__cell kgr-table__cell--actions">
                                        <kgr-icon-message-type
                                            v-if="announcement.processLog !== null"
                                            :title="announcement.processLog.messageText"
                                            :type="announcement.processLog.messageType"
                                        />
                                        <kgr-icon-warning
                                            v-if="announcement.processLog !== null && announcement.processLog.instruction !== ''"
                                            :title="announcement.processLog.instruction"
                                        />
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </kgr-table>
                </div>
            </div>
            <div
                v-if="currentReportFiles.length > 0"
                style="margin-top:3em"
            >
                <kgr-table
                    class="kgr-table--block"
                >
                    <thead>
                        <tr class="kgr-table__row">
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartenReport.tableheader.inPeriod') }}
                            </kgr-th>
                            <kgr-th sortable>
                                {{ $t('basfi.kindergartenReport.tableheader.fileName') }}
                            </kgr-th>
                            <kgr-th />
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="file in currentReportFiles" :key="file.reportId">
                            <tr class="kgr-table__row">
                                <td class="kgr-table__cell">
                                    {{ file.inPeriod }}
                                </td>
                                <td class="kgr-table__cell">
                                    {{ file.name }}
                                </td>
                                <td class="kgr-table__cell">
                                    <kgr-icon-download
                                        @click="downloadReport(file.reportId)"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </kgr-table>
            </div>
        </template>
    </div>
</template>

<script>
import BasfiKindergartenReportRepository from '../../js/repository/BasfiKindergartenReportRepository';
import Error from '../../js/model/Error';
import IconMessageType from '../../components/basfi/IconMessageType.vue';
import IconWarning from '../../components/common/icon/IconWarning.vue';
import KgrIconActive from '../../components/common/icon/IconActive.vue';
import KgrIconDownload from '../../components/common/icon/IconDownload.vue';
import KgrNoteBox from '../../components/common/NoteBox.vue';
import KindergartenConfig from '../../js/dto/basfi/KindergartenConfig';
import Loading from '../../components/common/Loading';
import MonthOfYear from '../../js/date/MonthOfYear';
import MonthOfYearSelect from '../../components/common/form/select/MonthOfYearSelect.vue';

export default {
    components: {
        'kgr-icon-active': KgrIconActive,
        'kgr-loading': Loading,
        'kgr-note-box': KgrNoteBox,
        'kgr-month-of-year-select': MonthOfYearSelect,
        'kgr-icon-message-type': IconMessageType,
        'kgr-icon-warning': IconWarning,
        'kgr-icon-download': KgrIconDownload,
    },
    props: {
        config: {
            type: KindergartenConfig,
            required: true,
        },
        context: {
            type: String,
            validate(val) {
                return val === 'federation' || val === 'kindergarten';
            },
            required: true
        },
    },
    data() {
        return {
            inPeriods: [MonthOfYear.current()],
            inPeriodRangeStart: new MonthOfYear(1, 2022),
            inPeriodRangeEnd: MonthOfYear.current(),
            previewReport: null,
            reports: [],
            loadingInit: false,
            loadingPreview: false,
            error: new Error(),
        }
    },
    computed: {
        previewSelected() {
            return this.inPeriods.find(p => p.equals(MonthOfYear.current())) != null;
        },
        loading() {
            return this.loadingInit || this.loadingPreview;
        },
        noteBoxParams() {
            if (this.config === null || this.config.isEnabled()) {
                return null;
            }
            return {
                title: 'basfi.' + this.config.status + '.' + this.context + '.title',
                text: 'basfi.' + this.config.status + '.' + this.context + '.text',
            }
        },
        currentSingleReport() {
            if (this.inPeriods.length !== 1) {
                return null;
            }
            const inPeriod = this.inPeriods[0];
            if (inPeriod.equals(MonthOfYear.current())) {
                return this.previewReport;
            }
            return this.reports.find(r => r.inPeriod.equals(inPeriod)) || null;
        },
        currentReports() {
            const notEmptyInPeriods = this.inPeriods.filter(p => this.reports.find(r => r.inPeriod.equals(p)) != null);
            const reports = [];
            if (this.previewSelected && this.previewReport !== null) {
                reports.push(this.previewReport);
            }
            return reports.concat(notEmptyInPeriods.map(p => this.reports.find(r => r.inPeriod.equals(p))));
        },
        currentReportFiles() {
            return this.currentReports.filter(r => r.file !== null).map(r => {
                return {
                    name: r.file,
                    reportId: r.id,
                    inPeriod: r.inPeriod
                }
            });
        },
    },
    watch: {
        config(config) {
            if(!config.isIncomplete()) {
                this.loadReports();
            }
        }
    },
    created() {
        if(!this.config.isIncomplete()) {
            this.loadReports();
        }
    },
    methods: {
        downloadReport(reportId) {
            document.location = `/api/basfi/reports/${reportId}/file`;
        },
        loadReports() {
            this.loadingInit = true;
            this.previewReport = null;
            BasfiKindergartenReportRepository
                .findByKindergarten(this.config.kindergarten.id)
                .then(data => {
                    this.reports = data;
                    this.loadingInit = false;
                    this.handleMonthOfYearSelect();
                });
        },
        handleMonthOfYearSelect() {
            this.loadingPreview = true;

            if (!this.previewSelected || this.inPeriods.length === 0) {
                this.$nextTick(() => { //required to rerender table
                    this.loadingPreview = false;
                })
                return;

            }
            BasfiKindergartenReportRepository
                .buildPreview(this.config.kindergarten.id)
                .then(report => {
                    this.previewReport = report;
                    this.loadingPreview = false;
                })
                .catch(error => {
                    this.$notify.error(error.message)
                })
            ;
        },
        childUrl(childId) {
            if (this.context === 'federation') {
                return `/federation/login/child/${childId}`;
            }
            return `/backend/child/${childId}/carecosts`;
        },
    },
}
</script>
